import { Box3, Color, Mesh, PlaneGeometry, ShaderMaterial, Vector4 } from 'three';
import { PAL } from '../palette';
import type { CarModel } from './carModel';

/**
 * The car's contact shadow: a soft dark patch on the road under it, darkest where each tyre touches. The sun's
 * shadow falls long and off to one side (it's a low, golden sun) and the sky light brightens the road under the
 * car as much as anywhere else, so without it the car looks like it hovers. Shaped from the car's own wheels
 * and body; a child of the car's root, so it follows the road's slope and banking and turns with the car (not
 * of the chassis: the bounce and squash leave it on the road). One small draw.
 */
export interface ContactShadow {
  mesh: Mesh;
  setOpacity(o: number): void;
}

/** Just above the asphalt (laid 3 cm over the road's height, the lines at 3.5) and anything painted on it. */
const LIFT = 0.042;
/** Under the floor: soft and wide; under each tyre: darker and tighter. */
const BODY_DARK = 0.42;
const TYRE_DARK = 0.7;

export function makeContactShadow(model: CarModel): ContactShadow {
  model.root.updateMatrixWorld(true);
  const body = new Box3().setFromObject(model.chassis);
  const wheels = model.wheels.slice(0, 4).map((w) => {
    const b = new Box3().setFromObject(w.spin);
    const cx = (b.min.x + b.max.x) / 2;
    const cz = (b.min.z + b.max.z) / 2;
    // The patch: a little longer than the tyre's footprint (it's soft), as wide as the tyre.
    return new Vector4(cx, cz, w.radius * 0.62, Math.max(0.08, (b.max.z - b.min.z) * 0.62));
  });
  while (wheels.length < 4) wheels.push(new Vector4(0, 0, 0.001, 0.001));
  const m = 0.7;
  const x0 = Math.min(body.min.x, ...wheels.map((w) => w.x - w.z)) - m;
  const x1 = Math.max(body.max.x, ...wheels.map((w) => w.x + w.z)) + m;
  const z0 = Math.min(body.min.z, ...wheels.map((w) => w.y - w.w)) - m;
  const z1 = Math.max(body.max.z, ...wheels.map((w) => w.y + w.w)) + m;
  const geo = new PlaneGeometry(x1 - x0, z1 - z0);
  geo.rotateX(-Math.PI / 2);
  geo.translate((x0 + x1) / 2, LIFT, (z0 + z1) / 2);
  // The body's patch: the footprint between the wheels, a bit inside the bodywork's outline (wings and
  // nose overhang thin air).
  const span = (a: number, b: number, k: number): [number, number] => [(a + b) / 2, ((b - a) / 2) * k];
  const [bx, bhx] = span(Math.max(body.min.x, Math.min(...wheels.map((w) => w.x))), Math.min(body.max.x, Math.max(...wheels.map((w) => w.x))), 1.05);
  const [bz, bhz] = span(Math.min(...wheels.map((w) => w.y)), Math.max(...wheels.map((w) => w.y)), 0.95);
  const mat = new ShaderMaterial({
    uniforms: {
      uBody: { value: new Vector4(bx, bz, Math.max(bhx, 0.3), Math.max(bhz, 0.25)) },
      uWheels: { value: wheels },
      uColor: { value: new Color(PAL.brown) },
      uOpacity: { value: 1 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vP;
      void main() {
        vP = position.xz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform vec4 uBody;
      uniform vec4 uWheels[4];
      uniform vec3 uColor;
      uniform float uOpacity;
      varying vec2 vP;
      // 1 inside an ellipse-ish shape (a rounded rectangle for p > 2), fading out over 'soft' of its size.
      float blob(vec4 e, float p, float soft) {
        vec2 d = abs(vP - e.xy) / e.zw;
        float r = pow(pow(d.x, p) + pow(d.y, p), 1.0 / p);
        return 1.0 - smoothstep(1.0 - soft, 1.0 + soft, r);
      }
      void main() {
        float a = blob(uBody, 2.6, 0.75) * ${BODY_DARK.toFixed(3)};
        for (int i = 0; i < 4; i++) a = 1.0 - (1.0 - a) * (1.0 - blob(uWheels[i], 2.0, 0.55) * ${TYRE_DARK.toFixed(3)});
        gl_FragColor = vec4(uColor, a * uOpacity);
      }`,
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  const mesh = new Mesh(geo, mat);
  mesh.name = 'contactShadow';
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  mesh.renderOrder = 1;
  return {
    mesh,
    setOpacity: (o) => {
      mat.uniforms.uOpacity.value = o;
      mesh.visible = o > 0.02;
    },
  };
}
